import React, { useEffect, useState } from "react";
import { Button, EmptyState, Input, Modal, Skeleton, Tabs } from "../ui";
import { useAsync } from "../../hooks";
import { errorInfo } from "../../services/errors";

/**
 * เลือกบทบาท (persona) / สถานการณ์ — แท็บ "ของฉัน / สาธารณะ", ยกเลิกการใช้, สร้างใหม่แบบเร็ว
 * kind: "persona" | "scenario" ; load(scope) -> รายการ ; save(data) -> สร้างแล้วเลือกให้เลย ; onPick(item|null)
 */
export default function PickDialog({ open, kind, title, hint, value, load, save, onPick, onClose, characterId }) {
  const isPersona = kind === "persona";
  const [scope, setScope] = useState("mine");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", detail: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const list = useAsync(() => (open ? load(scope) : Promise.resolve([])), [open, scope]);
  useEffect(() => { if (open) { setCreating(false); setForm({ name: "", detail: "" }); setErr(""); } }, [open]);

  const choose = async (item) => {
    setBusy(true);
    setErr("");
    try { await onPick(item); onClose(); } catch (e) { setErr(errorInfo(e).message); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    setErr("");
    try {
      const item = await save({ name: form.name.trim(), detail: form.detail.trim(), ...(isPersona || !characterId ? {} : { characterId }) });
      await onPick(item);
      onClose();
    } catch (e) {
      setErr(errorInfo(e).message);
    } finally {
      setBusy(false);
    }
  };
  const canCreate = form.name.trim() && (isPersona || form.detail.trim());

  return (
    <Modal open={open} title={title} onClose={onClose}
      footer={creating ? <><Button onClick={() => setCreating(false)}>กลับ</Button><Button variant="primary" disabled={busy || !canCreate} onClick={create}>สร้างและใช้เลย</Button></> : <Button onClick={onClose}>ปิด</Button>}>
      {creating ? (
        <>
          <Input label={isPersona ? "ชื่อบทบาทของคุณ" : "ชื่อสถานการณ์"} maxLength={isPersona ? 40 : 60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <Input multiline rows={5} label={isPersona ? "รายละเอียด (ไม่บังคับ)" : "รายละเอียดฉาก"} maxLength={isPersona ? 2000 : 3000} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })}
            hint={isPersona ? "เช่น อายุ นิสัย ความสัมพันธ์กับตัวละคร" : "บอกสถานที่ เวลา และสิ่งที่เกิดขึ้นตอนเริ่มเรื่อง"} />
        </>
      ) : (
        <>
          {hint ? <p className="muted mb-16" style={{ fontSize: 13 }}>{hint}</p> : null}
          <div className="row-between mb-16">
            <Tabs tabs={[{ value: "mine", label: "ที่ฉันสร้าง" }, { value: "public", label: "สาธารณะ" }]} value={scope} onChange={setScope} />
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>+ สร้างใหม่</Button>
          </div>
          {list.loading ? <Skeleton height={70} /> : list.error ? <div className="error-text">{list.error}</div> : (
            <div className="stack">
              {value ? <button className="pick" disabled={busy} onClick={() => choose(null)}><span className="grow strong">{isPersona ? "ยกเลิกการใช้บทบาท" : "ยกเลิกสถานการณ์"}</span></button> : null}
              {(list.data || []).map((it) => (
                <button key={it.id} className={`pick${it.id === value ? " is-active" : ""}`} aria-pressed={it.id === value} disabled={busy} onClick={() => choose(it)}>
                  <span className="grow"><span className="strong">{it.name}</span><span className="muted clamp-2" style={{ display: "block", fontSize: 13 }}>{it.detail || "ไม่มีรายละเอียด"}</span></span>
                </button>
              ))}
              {!(list.data || []).length ? <EmptyState icon={isPersona ? "🪪" : "🎬"} title={scope === "mine" ? "ยังไม่มีที่คุณสร้าง" : "ยังไม่มีรายการสาธารณะ"}>{scope === "mine" ? "กด + สร้างใหม่ เพื่อเริ่มเลย" : null}</EmptyState> : null}
            </div>
          )}
        </>
      )}
      {err ? <div className="error-text mt-16" role="alert">{err}</div> : null}
    </Modal>
  );
}
